import { useCallback } from "react";
import type { ValueOf } from "ts-essentials";

import router from "@/__main__/router.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { GAME_SHORT_NAMES } from "@/app/constants.mjs";
import { findGameSymbol } from "@/util/game-route.mjs";
import { stringifyPath } from "@/util/path-regexp.mjs";

export const EVENT_USER_INTERACTION = "EVENT_USER_INTERACTION";

export type EventData = {
  /// a string describing what's been interacted with
  feature_name: string;
  /// string of the interaction type
  interaction_type: string;
  /// string of game this interaction is tied to (if applicable, it could be interactions out of game pages)
  game: string;
  /// string of route url where this interaction happened
  route?: string;
  /// array of route url parameters strings where this interaction happen
  route_params?: [string, string][];
  /// some extra optional info/payload about what was selected
  meta: Record<string, unknown>;
};

export type INTERACTION = ValueOf<typeof INTERACTION>;
export const INTERACTION = {
  CLICK: "click",
  HOVER: "hover",
} as const;

const NON_GAME_ROUTE = "hub";
export function sendInteractionEvent(
  featureName: string,
  meta: Record<string, unknown> = {},
  interactionType: INTERACTION = INTERACTION.CLICK,
) {
  const game =
    (GAME_SHORT_NAMES[findGameSymbol()!] as string) ?? NON_GAME_ROUTE;
  const route = router.route;
  eventBus.emit(EVENT_USER_INTERACTION, {
    feature_name: featureName,
    interaction_type: interactionType,
    game,
    route: route?.path && stringifyPath(route.path),
    route_params:
      route?.searchParams && Array.from(route.searchParams.entries()),
    meta,
  } satisfies EventData);
}

// TODO: might be useful to add an intersection observer to track impressions, see battles and it's useBattlesAna1ytics hook
export function useInteractionEvent(featurePrefix?: string) {
  return useCallback(
    (
      featureName: string,
      meta: Record<string, unknown>,
      interactionType: INTERACTION,
    ) => {
      sendInteractionEvent(
        featurePrefix ? `${featurePrefix}-${featureName}` : featureName,
        meta,
        interactionType,
      );
    },
    [featurePrefix],
  );
}
